import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/app/store';
import {
  clearCurrentSession,
  exportSessionChatComplete,
  exportSessionProspects,
  fetchReplayDownload,
  fetchSessionDetailsData,
  fetchSessionParticipants,
  setCurrentParticipantsPage,
} from '../store/analyticsSlice';
import { selectCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { WebinarStatusTag } from '../components/WebinarStatusTag';
import { SessionKpiGrid } from '../components/SessionKpiGrid';
import { SessionTimelineChart } from '../components/SessionTimelineChart';
import { ParticipantDetailTable } from '../components/ParticipantDetailTable';
import { DeviceDonutChart } from '../components/DeviceDonutChart';
import { ArrowLeft, Calculator, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { ParticipantDetailSidebar } from '@/features/analytics/components/ParticipantDetailSidebar.tsx';
import { PostLiveFeedbackDialog } from '../components/PostLiveFeedbackDialog';
import { TrustpilotReviewDialog } from '../components/TrustpilotReviewDialog';
import { Button } from '@/components/ui/button';
import { getFormattedDate } from '@/lib/utils';
import { isNotStartedSession } from '@/lib/analyticsSessionStatus';
import { useLiveCloseNavigationFlag } from '@/lib/useLiveCloseNavigationFlag';
import { selectPostLiveSolicitation } from '@/lib/postLiveSolicitation';
import { useTrustpilotReviewRequest } from '../hooks/useTrustpilotReviewRequest';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import NotFoundPage from '@/pages/NotFound.tsx';
import Bugsnag from '@bugsnag/js';

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

/** Message volontairement fixe : Bugsnag regroupe alors tous les échecs en une seule issue. */
const REPLAY_FAILURE_MESSAGE = 'Replay generation failed';

/** Le back répond 200 avec un statut en échec : sans cette remontée, personne n'apprend la panne. */
const reportReplayFailure = (metadata: Record<string, unknown>): void => {
  if (!isBugsnagEnabled) return;
  Bugsnag.notify(new Error(REPLAY_FAILURE_MESSAGE), (event) => {
    event.severity = 'warning';
    event.addMetadata('replayGeneration', metadata);
  });
};

export const SessionDetail = () => {
  const { t } = useTranslation('common');
  const { t: tA } = useTranslation('analytics');
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const dispatchAction = useDispatch<AppDispatch>();

  /** Posé par la page Live à la clôture : sans lui, la sollicitation Trustpilot n'a pas de contexte. */
  const isReturningFromLive = useLiveCloseNavigationFlag();
  const { status: trustpilotStatus, request: trustpilotRequest } =
    useTrustpilotReviewRequest(isReturningFromLive);
  const solicitation = selectPostLiveSolicitation({ isReturningFromLive, trustpilotStatus });

  const activeWorkspaceEntity = useSelector(selectCurrentWorkspace);
  const {
    currentSession,
    participantsData,
    participantsLoading,
    replayDownload,
    prospectsExport,
    chatExport,
  } = useSelector((state: RootState) => state.analytics);

  const hasFetchedDataFlag = useRef(false);

  useEffect(() => {
    if (activeWorkspaceEntity?.id && sessionId && !hasFetchedDataFlag.current) {
      const workspaceIdentifierValue = activeWorkspaceEntity.id;
      const sessionIdentifierValue = Number.parseInt(sessionId);

      dispatchAction(
        fetchSessionDetailsData({
          workspaceId: workspaceIdentifierValue,
          sessionId: sessionIdentifierValue,
        }),
      );
      dispatchAction(
        fetchSessionParticipants({
          workspaceId: workspaceIdentifierValue,
          sessionId: sessionIdentifierValue,
          page: 0,
          size: 50,
        }),
      );

      hasFetchedDataFlag.current = true;
    }

    return () => {
      dispatchAction(clearCurrentSession());
      hasFetchedDataFlag.current = false;
    };
  }, [dispatchAction, activeWorkspaceEntity?.id, sessionId]);

  const sessionHeader = currentSession.data?.header;
  const replayStatus = sessionHeader?.desktopReplayStatus;
  const hasReplayDownloadUrl = Boolean(sessionHeader?.desktopReplayDownloadUrl);

  useEffect(() => {
    if (replayStatus !== 'FAILED' || hasReplayDownloadUrl) return;
    reportReplayFailure({
      workspaceId: activeWorkspaceEntity?.id,
      webinarId: sessionHeader?.webinarId,
      sessionId,
      status: replayStatus,
    });
  }, [
    replayStatus,
    hasReplayDownloadUrl,
    activeWorkspaceEntity?.id,
    sessionHeader?.webinarId,
    sessionId,
  ]);

  const handlePageChange = (targetPageIndex: number) => {
    if (activeWorkspaceEntity?.id && sessionId) {
      dispatchAction(setCurrentParticipantsPage(targetPageIndex));
      dispatchAction(
        fetchSessionParticipants({
          workspaceId: activeWorkspaceEntity.id,
          sessionId: Number.parseInt(sessionId),
          page: targetPageIndex,
          size: 50,
        }),
      );
    }
  };

  if (currentSession.loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px] w-full">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (currentSession.error || !currentSession.data) {
    const isNotFound = currentSession.error === 'errors.notFound';
    return (
      <NotFoundPage
        fullscreen={false}
        title={isNotFound ? tA('session.detail.notFoundTitle') : tA('session.detail.errorTitle')}
        description={
          isNotFound
            ? tA('session.detail.notFoundDescription')
            : t(currentSession.error || 'errors.generic')
        }
        homeHref="/analytics?tab=past-webinars"
        homeLabel={tA('session.detail.backToList')}
      />
    );
  }

  const sessionDataPayload = currentSession.data;

  const handleDownloadReplay = (type: 'desktop' | 'mobile') => {
    if (!activeWorkspaceEntity?.id || !sessionId) return;
    dispatchAction(
      fetchReplayDownload({
        workspaceId: activeWorkspaceEntity.id,
        sessionId: Number.parseInt(sessionId),
        type,
      }),
    );
  };

  const isReplayDownloading = replayDownload.loading;

  /** En échec on n'affiche rien au client : la panne part en alerte Bugsnag et se relance côté back. */
  const isReplayGenerationRunning = !hasReplayDownloadUrl && replayStatus === 'PROCESSING';

  const handleExportProspects = async () => {
    if (!activeWorkspaceEntity?.id || !sessionId) return;
    try {
      await dispatchAction(
        exportSessionProspects({
          workspaceId: activeWorkspaceEntity.id,
          sessionId: Number.parseInt(sessionId, 10),
        }),
      ).unwrap();
      toast.success(tA('session.detail.exportSuccess'));
    } catch {
      toast.error(tA('session.detail.exportError'));
    }
  };

  const handleExportChat = async () => {
    if (!activeWorkspaceEntity?.id || !sessionId) return;
    try {
      await dispatchAction(
        exportSessionChatComplete({
          workspaceId: activeWorkspaceEntity.id,
          sessionId: Number.parseInt(sessionId, 10),
        }),
      ).unwrap();
      toast.success(tA('session.detail.chatExportSuccess'));
    } catch {
      toast.error(tA('session.detail.chatExportError'));
    }
  };
  return (
    <div className="flex flex-col gap-8 w-full max-w-full overflow-hidden pb-20 px-2 md:px-4">
      <div className="flex flex-col gap-6">
        <button
          onClick={() => navigate('/analytics?tab=past-webinars')}
          className="flex cursor-pointer items-center gap-2 text-role-tertiary hover:text-dark group transition-all w-fit"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {tA('session.detail.backToList')}
          </span>
        </button>
        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
          <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-dark break-words">
                {sessionDataPayload.header.title}
              </h1>
              <WebinarStatusTag
                sessionType={sessionDataPayload.header.type}
                status={sessionDataPayload.header.status}
              />
            </div>
            <p className="text-role-tertiary text-sm font-medium">
              {getFormattedDate(
                sessionDataPayload.header.projectionDate,
                sessionDataPayload.header.timezone,
              )}
              {/* La durée affichée est celle autorisée par le forfait, pas un temps de live réel. */}
              {!isNotStartedSession(sessionDataPayload.header.status) && (
                <>
                  {' '}
                  — {sessionDataPayload.header.totalDurationMinutes} {t('time.minutesSuffix')}
                </>
              )}
            </p>
            {isNotStartedSession(sessionDataPayload.header.status) && (
              <p className="text-role-tertiary text-sm">{tA('session.detail.notStartedNotice')}</p>
            )}
            {isReplayGenerationRunning && (
              <p className="text-role-tertiary text-sm">{tA('session.detail.replayProcessing')}</p>
            )}
            <div className="flex md:items-center flex-col md:flex-row gap-2">
              {sessionDataPayload.header?.desktopReplayDownloadUrl != null &&
                sessionDataPayload.header.desktopReplayDownloadUrl !== '' && (
                  <Button
                    onClick={() => handleDownloadReplay('desktop')}
                    disabled={
                      sessionDataPayload.header.status !== 'FINISHED' || isReplayDownloading
                    }
                  >
                    <Download className="w-4 h-4" /> {tA('session.detail.downloadReplay')}
                  </Button>
                )}
              <Button
                type="button"
                onClick={() => void handleExportChat()}
                disabled={chatExport.loading || sessionDataPayload.header.status !== 'FINISHED'}
                variant="outline"
              >
                <FileText className="w-4 h-4" /> {tA('session.detail.downloadChat')}
              </Button>
              <Button
                type="button"
                onClick={() => void handleExportProspects()}
                disabled={
                  prospectsExport.loading || sessionDataPayload.header.status !== 'FINISHED'
                }
                variant="outline"
              >
                <FileSpreadsheet className="w-4 h-4" /> {tA('session.detail.exportData')}
              </Button>
            </div>
          </div>

          {!isNotStartedSession(sessionDataPayload.header.status) && (
            <button
              onClick={() => navigate(`/analytics?tab=comparator&sessionAId=${sessionId}`)}
              className="flex items-center cursor-pointer hover:shadow justify-center gap-2 px-4 py-2 bg-white border border-medium rounded-xl text-xs font-bold text-dark hover:bg-theme-light shadow-sm"
            >
              <Calculator className="w-4 h-4" /> {tA('session.detail.compare')}
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col xl:flex-row gap-6 min-w-0">
        <div className="flex-1 min-w-0">
          <SessionKpiGrid
            summary={sessionDataPayload.summary}
            status={sessionDataPayload.header.status}
          />
        </div>
        <div className="w-full xl:w-[360px] min-w-0">
          <DeviceDonutChart data={sessionDataPayload.deviceBreakdown} />
        </div>
      </div>
      <div className="w-full min-w-0">
        <SessionTimelineChart
          data={sessionDataPayload.timeline}
          interventions={sessionDataPayload.timelineInterventions}
          timezone={sessionDataPayload.header.timezone}
          status={sessionDataPayload.header.status}
        />
      </div>
      <div className="flex flex-col gap-6">
        <h3 className="text-xl font-semibold text-dark">
          {tA('session.detail.participantsList', { count: participantsData.totalElements })}
        </h3>
        <ParticipantDetailTable
          participants={participantsData.items}
          totalElements={participantsData.totalElements}
          totalPages={participantsData.totalPages}
          currentPage={participantsData.currentPage}
          onPageChange={handlePageChange}
          isLoading={participantsLoading}
        />
      </div>
      <ParticipantDetailSidebar />
      {solicitation === 'internal' && (
        <PostLiveFeedbackDialog
          workspaceId={activeWorkspaceEntity?.id}
          sessionId={sessionId ? Number.parseInt(sessionId, 10) : undefined}
          isSessionFinished={sessionDataPayload.header.status === 'FINISHED'}
          isSessionStarted={
            sessionDataPayload.header.status === 'FINISHED' ||
            sessionDataPayload.header.status === 'IN_PROGRESS'
          }
        />
      )}
      {solicitation === 'trustpilot' && trustpilotRequest && (
        <TrustpilotReviewDialog request={trustpilotRequest} />
      )}
    </div>
  );
};
